import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LineReveal, FadeUp } from "../components/Reveal";

const sections = [
  {
    id: "terms",
    number: "01",
    title: "Terms & Conditions",
    updated: "Effective December 2025",
    blocks: [
      {
        h: "Acceptance of Terms",
        p: "By accessing or using CareerFactors (the “Service”), you agree to be bound by these Terms & Conditions. If you do not agree, please refrain from using the Service. These terms apply to all visitors, users, students, aspirants, professionals, parents, and institutional partners.",
      },
      {
        h: "Services Offered",
        p: "CareerFactors provides career counselling, psychometric assessments, stream and college selection guidance, career-transition coaching, and institutional consulting. Sessions may be delivered online or onsite. The scope, price, and duration of each engagement will be shared before you confirm a booking.",
      },
      {
        h: "Bookings & Payments",
        p: "Free discovery calls may be booked without payment. Paid engagements require confirmation of scope and fees in writing. All fees are payable in advance unless expressly agreed otherwise. Prices are quoted in INR unless stated.",
      },
      {
        h: "Rescheduling & Cancellations",
        p: "You may reschedule a confirmed session up to 24 hours in advance without charge. Cancellations made less than 24 hours before a session are non-refundable, but the session may be re-credited at our discretion. No-shows are treated as cancellations.",
      },
      {
        h: "User Responsibilities",
        p: "You agree to provide accurate information during assessments and sessions, to respect the time and confidentiality of your counsellor, and to not share proprietary assessment reports, frameworks, or session recordings without prior written consent.",
      },
      {
        h: "Intellectual Property",
        p: "All content on this website, all assessment frameworks, roadmaps, and materials shared during sessions are the intellectual property of CareerFactors, unless otherwise noted. Personal use is permitted; commercial reuse, resale, or redistribution is not.",
      },
      {
        h: "Limitation of Liability",
        p: "CareerFactors provides guidance based on assessment, market knowledge, and experience. We do not guarantee any specific career outcome, placement, admission, or examination result. To the maximum extent permitted by law, our liability under any engagement is limited to the fees paid for that engagement.",
      },
      {
        h: "Governing Law",
        p: "These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts at the registered address of CareerFactors.",
      },
      {
        h: "Changes to These Terms",
        p: "We may update these terms from time to time. Material changes will be reflected on this page with a revised effective date. Continued use of the Service after changes constitutes acceptance.",
      },
    ],
  },
  {
    id: "privacy",
    number: "02",
    title: "Privacy Policy",
    updated: "Effective December 2025",
    blocks: [
      {
        h: "Information We Collect",
        p: "We collect information you provide directly — such as your name, email, phone number, academic background, career goals, and assessment responses — as well as technical information such as browser type, device, and pages visited. This helps us prepare for your session and improve the Service.",
      },
      {
        h: "How We Use Your Information",
        p: "Your information is used strictly to deliver the services you have requested — scheduling, assessment, counselling, roadmap preparation, and follow-up. With your explicit consent, we may share anonymised insights with partner institutions.",
      },
      {
        h: "Confidentiality",
        p: "Every session is treated as confidential. Notes and assessment results are stored securely and shared only with the counsellor(s) working with you. We do not sell your data, ever.",
      },
      {
        h: "Data Retention",
        p: "We retain your data for as long as necessary to provide the Service and for legitimate business or legal purposes. You may request deletion of your data at any time by writing to us.",
      },
      {
        h: "Third-Party Services",
        p: "We may use third-party tools (scheduling, analytics, email) to operate the Service. These providers are bound by their own privacy policies and are selected for their commitment to data protection.",
      },
      {
        h: "Cookies",
        p: "We may use cookies and similar technologies to remember preferences and understand how our website is used. You can control cookies through your browser settings.",
      },
      {
        h: "Your Rights",
        p: "You have the right to access, correct, or delete your personal information, and to withdraw consent for its processing at any time. Requests can be sent to hello@careerfactors.co.",
      },
      {
        h: "Contact",
        p: "For any privacy-related question, please write to hello@careerfactors.co. We respond within two working days.",
      },
    ],
  },
  {
    id: "disclaimer",
    number: "03",
    title: "Disclaimer",
    updated: "Effective December 2025",
    blocks: [
      {
        h: "Guidance, Not Guarantees",
        p: "CareerFactors offers career guidance, coaching, and consulting. Our recommendations are informed by assessment, market experience, and industry insight — but every career outcome depends on individual effort, external circumstances, and market conditions beyond our control. No specific job, admission, salary, or examination result is guaranteed.",
      },
      {
        h: "Not Financial, Legal, or Medical Advice",
        p: "Nothing on this website or in our sessions constitutes financial, legal, or medical advice. Where such advice is needed, please consult a qualified professional in the relevant field.",
      },
      {
        h: "Third-Party References",
        p: "References to companies (including Amazon, BP, and Microsoft) reflect roles into which candidates have been placed through the founder&rsquo;s recruitment work. These references do not imply endorsement by or a formal partnership with those companies.",
      },
      {
        h: "External Links",
        p: "Our website may contain links to third-party websites. We do not control and are not responsible for the content, policies, or practices of those websites.",
      },
    ],
  },
];

export default function Legal() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) {
      // small delay so layout settles + Lenis has time
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [hash]);

  return (
    <main data-testid="legal-page" className="pt-[112px]">
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="rule" /><span className="overline">Legal Notices</span>
          </div>
          <h1 className="font-serif font-light tracking-[-0.02em] leading-[0.94] text-[12vw] md:text-[8.5vw]">
            <LineReveal
              lines={[
                <>The <em className="italic text-[#C26D5C]">fine</em> print,</>,
                <>set in <em className="italic">plain</em> type.</>,
              ]}
              stagger={0.12}
              delay={0.1}
            />
          </h1>
          <FadeUp delay={0.4}>
            <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-[#1A1C1A]/80">
              Terms, privacy, and disclaimer — one page, plainly written. If anything here is unclear, write to us at hello@careerfactors.co and we&rsquo;ll clarify.
            </p>

            <nav className="mt-10 flex flex-wrap items-center gap-3" aria-label="Legal sections">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`legal-jump-${s.id}`}
                  className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-[#1A1C1A] hover:bg-[#1A1C1A] hover:text-[#F4F2F0] transition-colors"
                >
                  {s.number} · {s.title}
                </a>
              ))}
            </nav>
          </FadeUp>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="border-t border-[#1A1C1A]">
            {sections.map((s) => (
              <div key={s.id} id={s.id} className="border-b border-[#1A1C1A] py-14 md:py-20 scroll-mt-32">
                <FadeUp>
                  <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 md:col-span-3">
                      <div className="font-serif text-6xl md:text-7xl font-light text-[#C26D5C] leading-none">
                        {s.number}
                      </div>
                      <h2 className="font-serif text-3xl md:text-4xl font-light mt-6 tracking-tight leading-tight">
                        {s.title}
                      </h2>
                      <p className="mt-4 overline text-[#1A1C1A]/60">{s.updated}</p>
                    </div>

                    <div className="col-span-12 md:col-span-8 md:col-start-5 space-y-10">
                      {s.blocks.map((b, i) => (
                        <FadeUp key={b.h} delay={i * 0.03}>
                          <div className="border-t border-[#D1CDC7] pt-6">
                            <h3 className="font-serif text-xl md:text-2xl font-light tracking-tight mb-3">
                              {b.h}
                            </h3>
                            <p
                              className="text-base leading-relaxed text-[#1A1C1A]/85"
                              dangerouslySetInnerHTML={{ __html: b.p }}
                            />
                          </div>
                        </FadeUp>
                      ))}
                    </div>
                  </div>
                </FadeUp>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
